import { yupResolver } from "@hookform/resolvers/yup";
import { ReactElement, useCallback, useMemo } from "react";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import * as yup from "yup";

import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  Text,
  useDisclosure,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
  useToast,
  ButtonGroup,
  UseDisclosureProps,
  Tooltip,
  Wrap,
  WrapItem,
  Stack,
  FormLabel,
} from "@chakra-ui/react";

import { useCustomerContext } from "contexts/CustomerProvider";
import SubmitButton from "components/SubmitButton";
import useEditUserPermissions from "hooks/api/customers/useEditUserPermissions";
import useResendInvite from "hooks/api/customers/useResendInvite";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import PermissionsCheckboxSection from "../InviteNewUserModal/PermissionsCheckboxSection";
import RemoveUserConfirmation, {
  REMOVE_USER_ERROR_MESSAGE,
} from "./RemoveUserConfirmation";
import ResendInvite from "./ResendInvite";
import EditButton from "routes/contracts/details/tabs/tabPanels/orders/components/EditButton";
import {
  permissionKeyToPermissionValue,
  permissionValuesToPermissionsObject,
} from "services/customerPermissionsHelpers";
import { SchemaValidationError } from "services/apiErrors";
import { USER_PENDING_STATUS } from "../UserManagementTable";
import { mobileStyleBreakpoint } from "util/styleHelpers";
import { useAuth } from "contexts/AuthProvider";

const buttonGroupPadding = mobileStyleBreakpoint(0, 6);
const modalPadding = mobileStyleBreakpoint(4, 6);
const modalSize = mobileStyleBreakpoint("md", "xl");
const closeButtonMargin = mobileStyleBreakpoint(0, 2);

const editUserPermissionsSchema = yup.object().shape({
  readAccess: yup.bool().required(),
  addEditOrders: yup.bool().required(),
  signContracts: yup.bool().required(),
  userManagement: yup.bool().required(),
  requestContract: yup.bool().required(),
});

export const EditUserPermissionsModal = ({
  onShowRemoveConfirmation,
  isOpen,
  onClose,
  onSubmit,
  user,
}: {
  user: UserManagementDetails;
  onSubmit: SubmitHandler<UserPermissionsFlags>;
  onShowRemoveConfirmation: () => void;
} & Required<Pick<UseDisclosureProps, "isOpen" | "onClose">>): ReactElement => {
  const { selectedCustomers } = useCustomerContext();
  const { userId: loggedInUser } = useAuth();
  const {
    formState: { isSubmitting, isDirty },
    handleSubmit,
  } = useFormContext<UserPermissionsFlags>();

  const { mutateAsync: onResendInvite, isLoading } = useResendInvite();

  const toast = useToast();

  const onClickResendInvites = useCallback(() => {
    onResendInvite(user.userId)
      .then(() => {
        toast({
          title: "Success!",
          description: `Invitation has been resent to ${user.firstName} ${user.lastName}'s email`,
          status: "success",
          duration: 4000,
          position: "top",
        });
        onClose();
      })
      .catch(() => {
        toast({
          title: "Error",
          description: `Failed to resend invitation`,
          status: "error",
          duration: 4000,
          position: "top",
        });
      });
  }, [
    onResendInvite,
    user.userId,
    user.firstName,
    user.lastName,
    toast,
    onClose,
  ]);

  const resendInvite =
    user.status === USER_PENDING_STATUS ? (
      <ResendInvite isLoading={isLoading} onClick={onClickResendInvites} />
    ) : null;

  const removeUserButton = useMemo(
    () => (
      <Button
        isDisabled={user.userId === loggedInUser}
        pl={buttonGroupPadding}
        variant="link"
        size="md"
        color="warningRed"
        onClick={onShowRemoveConfirmation}
      >
        Remove User
      </Button>
    ),
    [loggedInUser, onShowRemoveConfirmation, user.userId]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInBottom"
      size={modalSize}
    >
      <ModalOverlay />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalContent p={modalPadding} mx={4}>
          <ModalCloseButton m={closeButtonMargin} />
          <ModalHeader p={0}>
            <Stack spacing={4} align="flex-start">
              <Text textStyle="h4">{selectedCustomers[0]?.name}</Text>
              <Text textStyle="h3" fontWeight="bold">
                Edit User Permissions
              </Text>
            </Stack>
          </ModalHeader>
          <ModalBody p={0} my={4}>
            <Stack spacing={4}>
              <Wrap>
                <WrapItem
                  flex={1}
                  alignItems="flex-start"
                  flexDirection="column"
                >
                  <FormLabel>First Name</FormLabel>
                  <Text fontWeight="bold">{user.firstName}</Text>
                </WrapItem>
                <WrapItem
                  flex={1}
                  alignItems="flex-start"
                  flexDirection="column"
                >
                  <FormLabel>Last Name</FormLabel>
                  <Text fontWeight="bold">{user.lastName}</Text>
                </WrapItem>
              </Wrap>
              <Stack spacing={0}>
                <FormLabel>Phone #</FormLabel>
                <Text fontWeight="bold">{user.phoneNumber}</Text>
              </Stack>
              <Stack spacing={0}>
                <FormLabel>Display Name</FormLabel>
                <Text fontWeight="bold">{user.displayName}</Text>
              </Stack>
              <Stack spacing={0}>
                <HStack>
                  <FormLabel mb={0}>Email</FormLabel>
                  {resendInvite}
                </HStack>
                <Text fontWeight="bold">{user.email}</Text>
              </Stack>
              <Text textStyle="label2">
                User Permission Level (select one or more)
              </Text>
              <PermissionsCheckboxSection />
            </Stack>
          </ModalBody>
          <ModalFooter p={0}>
            <ButtonGroup spacing={4} width="100%">
              {user.userId === loggedInUser ? (
                <Tooltip label={REMOVE_USER_ERROR_MESSAGE} placement="top">
                  {removeUserButton}
                </Tooltip>
              ) : (
                removeUserButton
              )}
              <Spacer />
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <SubmitButton
                isDisabled={isSubmitting || !isDirty}
                isLoading={isSubmitting}
              >
                Save
              </SubmitButton>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};

type EditUserPermissionsWrapperProps = {
  user: UserManagementDetails;
};

const EditUserPermissionsWrapper = ({
  user,
  isOpen = true,
  onClose = () => {
    return;
  },
  onOpenRemoveConfirmation = () => {
    return;
  },
}: EditUserPermissionsWrapperProps &
  Pick<UseDisclosureProps, "isOpen" | "onClose"> & {
    onOpenRemoveConfirmation?: UseDisclosureProps["onClose"];
  }) => {
  const methods = useForm<UserPermissionsFlags>({
    resolver: yupResolver(editUserPermissionsSchema),
    defaultValues: permissionValuesToPermissionsObject(user.permissions ?? []),
  });

  const { setError } = methods;

  const selectedCustomerCode = useSelectedCustomerCode()[0];
  const { mutateAsync: updateUserPermissions } = useEditUserPermissions({
    userId: user.userId,
    email: user.email,
    displayName: user.displayName,
    cardCode: selectedCustomerCode,
    firstName: user.firstName,
    lastName: user.lastName,
    phoneNumber: user.phoneNumber,
  });
  const toast = useToast();
  const onSubmitUserPermissionsUpdate = useCallback(
    (data: UserPermissionsFlags) =>
      updateUserPermissions({
        readAccess: true,
        addEditOrders: data.addEditOrders,
        signContracts: data.signContracts,
        userManagement: data.userManagement,
        requestContract: data.requestContract,
      })
        .then(() => {
          toast({
            title: "Success!",
            description: "Permissions have been updated.",
            status: "success",
            duration: 4000,
            isClosable: true,
            position: "top",
          });
          onClose();
        })
        .catch((err) => {
          if (err instanceof SchemaValidationError) {
            err.errors?.forEach(({ propertyName, message }) => {
              if (
                propertyName &&
                permissionKeyToPermissionValue.hasOwnProperty(propertyName)
              ) {
                setError(propertyName as keyof UserPermissionsFlags, {
                  message: `${
                    permissionKeyToPermissionValue[
                      propertyName as keyof UserPermissionsFlags
                    ]
                  } ${message}`,
                  type: "manual",
                });
              }
            });
            toast({
              title: "Error",
              description: "Failed to update permissions",
              status: "error",
              duration: 3000,
              isClosable: true,
              position: "top",
            });
          }
        }),
    [onClose, setError, toast, updateUserPermissions]
  );

  return (
    <FormProvider {...methods}>
      <EditUserPermissionsModal
        onSubmit={onSubmitUserPermissionsUpdate}
        onShowRemoveConfirmation={() => {
          onClose();
          onOpenRemoveConfirmation();
        }}
        isOpen={isOpen}
        onClose={onClose}
        user={user}
      />
    </FormProvider>
  );
};

export const EditUserModalButton = (props: EditUserPermissionsWrapperProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isRemoveConfirmationOpen,
    onOpen: onOpenRemoveConfirmation,
    onClose: onCloseRemoveConfirmation,
  } = useDisclosure();
  return (
    <>
      <EditButton onClick={onOpen} />
      {isOpen ? (
        <EditUserPermissionsWrapper
          {...props}
          onClose={onClose}
          isOpen={isOpen}
          onOpenRemoveConfirmation={onOpenRemoveConfirmation}
        />
      ) : null}
      <RemoveUserConfirmation
        selectedUserId={props.user.userId}
        isOpen={isRemoveConfirmationOpen}
        onClose={onCloseRemoveConfirmation}
        customerName={`${props.user.firstName} ${props.user.lastName}`}
      />
    </>
  );
};
export default EditUserPermissionsWrapper;
